import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { useAdminAuthStore } from '../store/adminAuthStore';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const invitationNotice =
    (location.state as { notice?: string } | null)?.notice ||
    ((location.state as { invitationAccepted?: boolean } | null)?.invitationAccepted
      ? 'Registration complete. Sign in with your email and password.'
      : '');
  const invitedEmail = (location.state as { email?: string } | null)?.email || '';
  const { login, loginWithTwoFactor, isLoading, error, clearError, pendingTwoFactorToken } = useAdminAuthStore();

  const [formData, setFormData] = useState({
    email: invitedEmail,
    password: '',
    twoFactorCode: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    try {
      if (showTwoFactor && pendingTwoFactorToken) {
        await loginWithTwoFactor(pendingTwoFactorToken, formData.twoFactorCode.replace(/\s/g, ''));
        navigate('/dashboard', { replace: true });
        return;
      }

      const step = await login(formData.email, formData.password);

      if (step === 'needs-mfa-setup') {
        navigate('/setup-mfa', { replace: true });
        return;
      }

      if (step === 'needs-2fa') {
        setShowTwoFactor(true);
        setFormData((p) => ({ ...p, twoFactorCode: '' }));
        return;
      }

      navigate('/dashboard', { replace: true });
    } catch {
      /* error in store */
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/logo.png"
            alt="XChangeLab"
            className="h-10 w-auto object-contain"
          />
        </div>
        <p className="mt-6 text-center text-sm text-gray-600">
          Sign in to your admin account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {invitationNotice && !error && (
              <div className="rounded-md bg-green-50 p-4">
                <p className="text-sm font-medium text-green-800">{invitationNotice}</p>
              </div>
            )}
            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}

            {!showTwoFactor && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="admin@xchangelab.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="appearance-none block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {showTwoFactor && (
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  Open your authenticator app and enter the 6-digit code.
                </p>
                <label htmlFor="twoFactorCode" className="block text-sm font-medium text-gray-700">
                  Authenticator code
                </label>
                <div className="mt-1">
                  <input
                    id="twoFactorCode"
                    name="twoFactorCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={formData.twoFactorCode}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm tracking-widest text-center font-mono text-lg"
                    placeholder="000000"
                  />
                  <button
                    type="button"
                    className="mt-2 text-xs text-gray-500 hover:text-gray-800 underline"
                    onClick={() => {
                      setShowTwoFactor(false);
                      clearError();
                    }}
                  >
                    Back to password
                  </button>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={
                  isLoading ||
                  !!(showTwoFactor && pendingTwoFactorToken && formData.twoFactorCode.trim().length < 6)
                }
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Please wait…' : showTwoFactor ? 'Confirm' : 'Sign in'}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Admin Access Only</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
